import { ref, reactive, computed, onMounted, watch } from 'vue';
import { defineStore } from 'pinia';
import { useToast } from 'vue-toastification';
import { useProductStore } from './productsStore';

const toast = useToast();

export const useOrderStore = defineStore('orders', () => {

  const selectedSalesPerson = ref(null);
  const applySelectedSalesPersonForAll = ref(false);
  const productStore = useProductStore();

  function normalizeTax(tax) {
    return {
      type: tax.Description || tax.type || tax.TaxId || '',
      rate: Number(tax.Rate ?? tax.rate ?? tax.Amount ?? 0),
      amount: 0,
    };
  }

  function getProductStoreTaxes() {
    if (!Array.isArray(productStore.taxes) || productStore.taxes.length === 0) {
      return [];
    }

    return productStore.taxes
      .map(normalizeTax)
      .filter(tax => tax.type);
  }

  const state = reactive({
    InvoiceId: null,
    orderItems: [],
    approvalList: [],
    overallDiscount: ref(0),
    customer: reactive({
      id: '',
      name: '',
      lastName: '',
      phone: '',
      email: '',
      note: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    }),
    comments: [],
    payments: [],
    total: ref(0),
    taxes: getProductStoreTaxes(),
    receiptForReprint: null,
    returnPrint: null,
    couponCode: null,
    isManualOrder: false,
    addManualOrder: false,
    pickUpInStore: false,
    pickupLocation: null,
  });

  // Track items with invalid/empty prices (separate from reactive state)
  const invalidPriceFields = ref(new Set());

  console.log('Taxes in porduct store', productStore.taxes);
  console.log('Taxes in orderStore', state.taxes);

  const setReceiptForReprint = (receipt) => {
    state.receiptForReprint = receipt;
  };

  const clearReceiptForReprint = () => {
    state.receiptForReprint = null;
  };

  const clearReturnPrint = () => {
    state.returnPrint = null;
  }

  // Add or increment an order item
  function addOrderItem(item) {
    if (state.taxes.some(tax => tax.type === 'GST' && tax.rate === 5)) {
      console.log('Using default taxes. Ensure productStore.taxes is loaded.');
    } else {
      console.log('Using fixed taxes. Ensure productStore.taxes', state.taxes);
    }

    const existingItem = state.orderItems.find(orderItem =>
      orderItem.RetailSku === item.RetailSku
    );

    // const miscItem = state.orderItems.find(orderItem =>
    //   orderItem.Sku === '2'
    // );  

    // If the item being added is a miscItem (sku === '2') and a miscItem already exists,
    if (item.Sku === '2') {
      const miscItems = state.orderItems.filter(orderItem => orderItem.Sku === 'MISC-2');

      const newCounter = miscItems.length + 1;
      const newItem = {
        ItemId: item.Sku,
        uniqueId: `2-${Date.now()}`,
        Sku: item.RetailSku || item.Sku,
        ItemName: isManualOrderLoading.value ? `${item.Name}` : `${item.Name} ${newCounter}`,
        originalName: item.Name || '',
        ItemImage: item.ImageUrl,
        Qty: item.Qty || 1,
        MaxQty: item.MaxQty,
        Discount: item.Discount || 0,
        TaxesWaived: false,
        OriginalPrice: item.Price,
        Price: null,
        SalesPersonId: selectedSalesPerson.value?.SalesPersonId || null,
        taxRates: state.taxes.map(tax => ({
          type: tax.type,
          rate: tax.rate,
          amount: 0,
        })),
        RetailSku: item.RetailSku,
        isIncludeTaxChecked: false,
      };

      if (state.overallDiscount > 0) {
        newItem.Price = Number(
          (newItem.OriginalPrice * (1 - state.overallDiscount / 100)).toFixed(2)
        );
      } else {
        newItem.Price = Number(newItem.OriginalPrice);
      }

      if (applySelectedSalesPersonForAll.value && selectedSalesPerson.value) {
        newItem.SalesPersonId = selectedSalesPerson.value;
      }

      state.orderItems.push(newItem);
    } else if (item.Sku === 'TIP-99') {
      const tipItems = state.orderItems.filter(orderItem => orderItem.Sku === 'TIP-99');

      const newCounter = tipItems.length + 1;
      const newItem = {
        ItemId: item.Sku,
        uniqueId: `9-${Date.now()}`,
        Sku: item.RetailSku || item.Sku,
        ItemName: `${item.Name} ${newCounter}`,
        originalName: item.Name || '',
        ItemImage: item.ImageUrl,
        Qty: item.Qty || 1,
        MaxQty: item.MaxQty,
        Discount: 0,
        TaxesWaived: true,
        OriginalPrice: item.Price,
        Price: null,
        SalesPersonId: selectedSalesPerson.value?.SalesPersonId || null,
        taxRates: state.taxes.map(tax => ({
          type: tax.type,
          rate: 0,
          amount: 0,
        })),
        RetailSku: item.RetailSku,
        isIncludeTaxChecked: false,
      };

      if (state.overallDiscount > 0) {
        newItem.Price = Number(
          (newItem.OriginalPrice * (1 - state.overallDiscount / 100)).toFixed(2)
        );
      } else {
        newItem.Price = Number(newItem.OriginalPrice);
      }

      if (applySelectedSalesPersonForAll.value && selectedSalesPerson.value) {
        newItem.SalesPersonId = selectedSalesPerson.value;
      }

      state.orderItems.push(newItem);
    } else if (item.Sku.startsWith('BSUR')) {
      // If the item being added is a BSUR (sku === 'BSUR') and a BSUR already exists,
      const bsurItems = state.orderItems.filter(orderItem => orderItem.Sku.startsWith('BSUR'));
      const newCounter = bsurItems.length + 1;

      const newItem = {
        ItemId: item.Sku,
        uniqueId: `1-${Date.now()}`,
        Sku: item.RetailSku || item.Sku,
        ItemName: `${item.Name} ${newCounter}`,
        ItemImage: item.ImageUrl,
        Qty: item.Qty || 1,
        MaxQty: item.MaxQty,
        Discount: item.Discount || 0,
        TaxesWaived: false,
        OriginalPrice: item.Price,
        Price: null,
        SalesPersonId: selectedSalesPerson.value?.SalesPersonId || null,
        taxRates: state.taxes.map(tax => ({
          type: tax.type,
          rate: tax.rate,
          amount: 0,
        })),
        RetailSku: item.RetailSku,
        isIncludeTaxChecked: false,
        storedBarcodeCode: item.storedBarcodeCode || null,
      };

      if (state.overallDiscount > 0) {
        newItem.Price = Number(
          (newItem.OriginalPrice * (1 - state.overallDiscount / 100)).toFixed(2)
        );
      } else {
        newItem.Price = Number(newItem.OriginalPrice);
      }

      if (applySelectedSalesPersonForAll.value && selectedSalesPerson.value) {
        newItem.SalesPersonId = selectedSalesPerson.value;
      }

      state.orderItems.push(newItem);

    } else if (existingItem) {
      // If the item already exists and it's not a miscItem, increase its quantity.
      existingItem.Qty++;
    } else {
      // If the item doesn't exist, add it as a new item.
      const newItem = {
        ItemId: item.Sku,
        uniqueId: item.Sku === '2' ? `2-${Date.now()}` : null,
        Sku: item.RetailSku || item.Sku,
        ItemName: item.Name,
        ItemImage: item.ImageUrl,
        Qty: item.Qty || 1,
        MaxQty: item.MaxQty || null,
        Discount: item.Discount || 0,
        TaxesWaived: false,
        OriginalPrice: item.Price,
        Price: null,
        RetailSku: item.RetailSku,
        SalesPersonId: selectedSalesPerson.value?.SalesPersonId || null,
        taxRates: state.taxes.map(tax => ({
          type: tax.type,
          rate: tax.rate,
          amount: 0,
        })),
        isIncludeTaxChecked: false,
      };
      if (state.overallDiscount > 0) {
        newItem.Price = Number(
          (newItem.OriginalPrice * (1 - state.overallDiscount / 100)).toFixed(2)
        );
      } else {
        newItem.Price = Number(newItem.OriginalPrice);
      }

      if (applySelectedSalesPersonForAll.value && selectedSalesPerson.value) {
        newItem.SalesPersonId = selectedSalesPerson.value;
      }

      state.orderItems.push(newItem);
    }

    calculateTaxes();
    saveOrderItemsToLocalStorage();
  }

  // For Manual Order Start
  function clearOrderItems() {
    state.orderItems = [];
  }

  function setTaxesFManualOrder(taxesFromApi) {
    if (!taxesFromApi || taxesFromApi.length === 0) {
      console.warn('No taxes provided in API response, using default taxes');
      return;
    }
    state.taxes = taxesFromApi.map(tax => ({
      type: tax.TaxId,
      rate: parseFloat(tax.Amount),
      amount: 0,
    }));

    console.log('Taxes set from manual order:', state.taxes);
  }

  function setCustomer(customer) {
    state.customer.id = customer.id || '';
    state.customer.name = customer.Name || '';
    state.customer.phone = customer.Phone || '';
    state.customer.email = customer.Email || '';
    state.customer.note = customer.Note || '';
  }

  const setSaleOrderNumber = (saleOrder) => {
    state.InvoiceId = saleOrder;
    saveOrderItemsToLocalStorage();
  }

  const setSalesPersonId = (salesPersonId) => {
    selectedSalesPerson.value = {
      SalesPersonId: salesPersonId
    };

    state.orderItems.forEach(item => {
      item.SalesPersonId = salesPersonId;
    });

    saveOrderItemsToLocalStorage();
  }

  // set overall discount

  const setOverallDiscount = (discount) => {
    state.overallDiscount = discount;
    state.orderItems.forEach(item => {
      item.Discount = discount;
      const newPrice = Number((item.OriginalPrice * (1 - discount / 100)).toFixed(2));
      
      // If tax is included in price, update both taxInclusivePrice and base price
      if (item.isIncludeTaxChecked) {
        item.taxInclusivePrice = newPrice;
        // Recalculate base price from tax-inclusive price
        const totalTaxRate = item.taxRates.reduce((sum, tax) => sum + (tax.rate || 0), 0);
        if (totalTaxRate > 0) {
          item.Price = Number((newPrice / (1 + totalTaxRate / 100)).toFixed(2));
        } else {
          item.Price = newPrice;
        }
      } else {
        item.Price = newPrice;
      }
    });
    calculateTaxes();
    saveOrderItemsToLocalStorage();
  }

  // For Manual Order End

  // Increment quantity of a specific order item
  function incrementOrderItem(item) {
    const existingItem = state.orderItems.find(orderItem =>
      (item.Sku === 'MISC-2') ? orderItem.uniqueId === item.uniqueId : orderItem.ItemId === item.ItemId
    );
    if (existingItem) {
      existingItem.Qty++;
      // if(existingItem.Qty > existingItem.MaxQty) {
      //   existingItem.Qty = existingItem.MaxQty;
      // }
      saveOrderItemsToLocalStorage();
      calculateTaxes();
    }
  }

  // Decrement quantity of a specific order item or delete if Qty is 1
  function decrementOrderItem(item) {
    const existingItem = state.orderItems.find(orderItem =>
      item.Sku === 'MISC-2' ? orderItem.uniqueId === item.uniqueId : orderItem.ItemId === item.ItemId
    );
    if (existingItem && existingItem.Qty > 1) {
      existingItem.Qty--;
      calculateTaxes();
      saveOrderItemsToLocalStorage();
    } else if (existingItem && existingItem.Qty === 1) {
      deleteOrderItem(existingItem);
    }
  }

  // Delete a specific order item
  function deleteOrderItem(item) {
    if (!item) {
      state.orderItems = [];
      state.approvalList = [];
      state.taxes = getProductStoreTaxes();
      state.total = 0;
      saveOrderItemsToLocalStorage();
      return;
    }

    const index = state.orderItems.findIndex(orderItem =>
      item.Sku === '2' ? orderItem.uniqueId === item.uniqueId : orderItem.ItemId === item.ItemId
    );

    if (index !== -1) {
      state.orderItems.splice(index, 1);

      state.approvalList = state.approvalList.filter(approval =>
        approval.ItemId !== item.ItemId
      );

      calculateTaxes();
      saveOrderItemsToLocalStorage();
    }

    // If all items are deleted, reset the overall discount and approval list
    if (state.orderItems.length === 0) {
      state.addManualOrder = state.addManualOrder ? state.addManualOrder : false;
      state.InvoiceId = null;
      state.overallDiscount = 0;
      state.pickUpInStore = false;
      state.pickupLocation = null;
      state.approvalList = [];
      if (!state.addManualOrder) {
        state.customer = {
          id: '',
          name: '',
          lastName: '',
          phone: '',
          email: '',
          note: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
        };
      }
      state.payments = [];
      state.comments = [];
      state.taxes = getProductStoreTaxes();
      state.total = 0;
    }
    saveOrderItemsToLocalStorage();
  }

  // this is to controle add manual order state
  // if clearCustomer is invoked, then creating a new manual order state is cleared 
  function clearCustomer() {
    if(isThisPaidManualOrder.value) {
        state.customer = {
        id: '',
        name: '',
        lastName: '',
        phone: '',
        email: '',
        note: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      };
      isThisPaidManualOrder.value = false;
      clearOrderItems();
      state.pickUpInStore = false;
      state.pickupLocation = null;
    } else {
      state.addManualOrder = false;
        state.customer = {
        id: '',
        name: '',
        lastName: '',
        phone: '',
        email: '',
        note: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      };
      state.pickUpInStore = false;
      state.pickupLocation = null;
    }
    state.isManualOrder = false;
    state.InvoiceId = null;
    saveOrderItemsToLocalStorage();
  }

  // Apply the selected salesperson to only the selected items in the order
  function setSelectedSalesPerson(salesPersonId, item) {
    const orderItem = state.orderItems.find(orderItem => orderItem.Sku === item.Sku);

    if (orderItem) {
      orderItem.SalesPersonId = salesPersonId;
    } else {
      console.error('Order item not found for the provided Sku.');
    }
    saveOrderItemsToLocalStorage();
  }


  // Apply the selected salesperson to all items in the order
  function applySalesPersonToAllItems(salesPersonId) {
    state.orderItems.forEach(item => {
      item.SalesPersonId = salesPersonId;
    });
    applySelectedSalesPersonForAll.value = true;
    saveOrderItemsToLocalStorage();
  }

  // Load the entire state from localStorage
  function loadOrderItemsFromLocalStorage() {
    const savedState = JSON.parse(localStorage.getItem('newOrder'));

    if (savedState) {
      // Merge savedState into state, but preserve state.taxes
      Object.assign(state, {
        ...savedState,
        taxes: savedState.taxes && Array.isArray(savedState.taxes)
          ? savedState.taxes.map(tax => normalizeTax(tax))
          : getProductStoreTaxes(),
      });

      console.log('Saved taxes from localStorage:', savedState.taxes);
      console.log('Updated state.taxes:', state.taxes);

      // Update each order item with taxRate1 and taxRate2
      state.orderItems.forEach(item => {
        // Ensure taxRate1 and taxRate2 are set based on state.taxes
        // item.taxRate1 = state.taxes[0]?.rate || 0;
        // item.taxRate2 = state.taxes[1]?.rate || 0;

        const comment = state.comments.find(c => c.item.sku === item.Sku);

        // If the comment is missing or empty, reset the discount
        if (!comment || !comment.text.trim()) {
          resetDiscount(item); // Reset discount if no valid comment
        } else {
          // If a valid comment exists, calculate price with discount
          if (item.Discount && item.OriginalPrice) {
            item.Price = (item.OriginalPrice * (1 - item.Discount / 100)).toFixed(2);
          }
        }
        console.log('TaxRate1 when load from LS', item.taxRate1);
      });

      // Recalculate prices for all items if overall discount is applied
      if (state.overallDiscount > 0) {
        state.orderItems.forEach(item => {
          item.Price = (item.OriginalPrice * (1 - state.overallDiscount / 100)).toFixed(2);
        });
      }

      calculateTaxes();
    } else {
      // Initialize default state if no saved state is found
      state.orderItems = [];
      state.overallDiscount = 0;
      state.customer = {
        id: '',
        name: '',
        lastName: '',
        phone: '',
        email: '',
        note: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      };
      state.comments = [];
      state.payments = [];
      state.total = 0;
      state.taxes = getProductStoreTaxes();
    }
  }
  
  // this function handles updating item from realtime service
  // it looks for matching sku and updates price, name, imageUrl, maxQty
  function applyRealtimeItemUpdate(payload) {
    if (!payload) {
      return false;
    }

    const sku = payload.retailSku || payload.RetailSku;
    if (!sku) {
      return false;
    }

    const item = state.orderItems.find(
      item => item.RetailSku === sku
    );

    if (!item) {
      return false;
    }

    const cachedItemRaw = localStorage.getItem('cachedProducts');
    if (cachedItemRaw) {
      let cachedItem;
      try {
        cachedItem = JSON.parse(cachedItemRaw);
      } catch {
        cachedItem = null;
      }

      if (cachedItem && typeof cachedItem === 'object') {
        const cachedProduct = cachedItem[sku];
        if (cachedProduct) {
          item.OriginalPrice = cachedProduct.Price;
          item.ItemName = cachedProduct.Name;
          item.ItemImage = cachedProduct.ImageUrl;
          item.MaxQty = cachedProduct.MaxQty;
        }

        // Update cachedProducts with realtime values as welgl
        if (cachedItem[sku]) {
          if (payload.price !== undefined) cachedItem[sku].Price = payload.price;
          if (payload.name !== undefined) cachedItem[sku].Name = payload.name;
          if (payload.imageUrl !== undefined) cachedItem[sku].ImageUrl = payload.imageUrl;
          if (payload.maxQty !== undefined) cachedItem[sku].MaxQty = payload.maxQty;

          localStorage.setItem('cachedProducts', JSON.stringify(cachedItem));
        }
      }
    }

    const previousPrice = item.Price;
    const previousName = item.ItemName;
    const previousImage = item.ItemImage;
    const previousMaxQty = item.MaxQty;

    if (payload.price !== undefined) item.Price = payload.price;
    if (payload.price !== undefined) item.OriginalPrice = payload.price;
    if (payload.name !== undefined) item.ItemName = payload.name;
    if (payload.imageUrl !== undefined) item.ItemImage = payload.imageUrl;
    if (payload.maxQty !== undefined) item.MaxQty = payload.maxQty;

    const hasChange =
      previousPrice !== item.Price ||
      previousName !== item.ItemName ||
      previousImage !== item.ItemImage ||
      previousMaxQty !== item.MaxQty;

    if (!hasChange) {
      return true;
    }

    // Save change to localStorage
    saveOrderItemsToLocalStorage();

    const updatedName = item.ItemName || previousName || sku;
    if (previousPrice !== item.Price) {
      toast.info(
        `${updatedName} \nprice updated:${previousPrice} → ${item.Price}`
      );
    } else {
      toast.info(`${updatedName} updated`);
    }

    return true;
  }

  // Functions to manage invalid price fields
  const addInvalidPriceField = (itemId) => {
    invalidPriceFields.value.add(itemId);
  };

  const removeInvalidPriceField = (itemId) => {
    invalidPriceFields.value.delete(itemId);
  };

  const hasInvalidPrice = (itemId) => {
    return invalidPriceFields.value.has(itemId);
  };

  // Function to recalculate taxes based on the current rates (excluding invalid price items)
  function calculateTaxes() {
    state.taxes.forEach(tax => {
      tax.amount = state.orderItems.reduce((total, item) => {
        // Skip items with invalid prices
        const itemId = item.uniqueId || item.ItemId;
        if (invalidPriceFields.value.has(itemId)) {
          return total;
        }
        
        // Skip items with tax included in price
        if (item.isIncludeTaxChecked) {
          return total;
        }
        
        const itemTaxRate = item.taxRates.find(t => t.type === tax.type)?.rate || 0;
        return total + (item.Price * item.Qty * itemTaxRate / 100);
      }, 0).toFixed(2);
    });
  }


  function updateItemTaxRate(item, taxType, newRate) {
    const taxRate = item.taxRates.find(t => t.type === taxType);
    if (taxRate) {
      taxRate.rate = newRate;
      
      // If tax is included in price, recalculate base price when tax rates change
      if (item.isIncludeTaxChecked && item.taxInclusivePrice) {
        const totalTaxRate = item.taxRates.reduce((sum, tax) => sum + (tax.rate || 0), 0);
        if (totalTaxRate > 0) {
          item.Price = Number((item.taxInclusivePrice / (1 + totalTaxRate / 100)).toFixed(2));
        } else {
          item.Price = item.taxInclusivePrice;
        }
      }
      
      calculateTaxes();
      saveOrderItemsToLocalStorage();
    }
  }


  // Function to update GST rate and recalculate taxes
  function updateGstRate(rate) {
    const gstTax = state.taxes.find(tax => tax.type === 'GST');
    if (gstTax) {
      gstTax.rate = Number(rate);
      calculateTaxes();
    }
  }

  // hnadle update PST rate and recalculate taxes
  function updatePstRate(rate) {
    const pstTax = state.taxes.find(tax => tax.type === 'PST');
    if (pstTax) {
      pstTax.rate = Number(rate);
      calculateTaxes();
    }
  }

  const getItemTaxAmounts = (item) => {
    const tax1Amount = (item.Qty * item.Price * (item.taxRate1 / 100)).toFixed(2);
    const tax2Amount = (item.Qty * item.Price * (item.taxRate2 / 100)).toFixed(2);
    return { tax1Amount, tax2Amount };
  };

  function resetTaxRate(item) {
    // Ensure item exists and has taxRates, or initialize them
    if (!item) return;

    if (!item.taxRates || !Array.isArray(item.taxRates)) {
      item.taxRates = state.taxes.map(tax => ({
        type: tax.type,
        rate: tax.rate,
        amount: 0
      }));
      return;
    }

    // Reset each tax rate to its default value
    state.taxes.forEach(defaultTax => {
      const itemTaxRate = item.taxRates.find(t => t.type === defaultTax.type);

      if (itemTaxRate) {
        itemTaxRate.rate = defaultTax.rate;
      } else {
        // If tax type doesn't exist in item, add it
        item.taxRates.push({
          type: defaultTax.type,
          rate: defaultTax.rate,
          amount: 0
        });
      }
    });

    // If tax is included in price, recalculate base price when tax rates are reset
    if (item.isIncludeTaxChecked && item.taxInclusivePrice) {
      const totalTaxRate = item.taxRates.reduce((sum, tax) => sum + (tax.rate || 0), 0);
      if (totalTaxRate > 0) {
        item.Price = Number((item.taxInclusivePrice / (1 + totalTaxRate / 100)).toFixed(2));
      } else {
        item.Price = item.taxInclusivePrice;
      }
    }

    calculateTaxes();
    saveOrderItemsToLocalStorage();
  }

  // handle update state total
  function updateOrderTotal(newTotal) {
    const roundedTotal = parseFloat(newTotal.toFixed(2));
    state.total = roundedTotal;
    // saveOrderItemsToLocalStorage();
  }

  const getTotalTax1Amount = computed(() => {
    const taxType1 = state.taxes[0]?.type;
    if (!taxType1) return 0;

    return state.orderItems.reduce((total, item) => {
      // Skip items with tax included in price
      if (item.isIncludeTaxChecked) {
        return total;
      }
      
      const taxRate = item.taxRates.find(tax => tax.type === taxType1)?.rate || 0;
      return total + (item.Price * item.Qty * (taxRate / 100));
    }, 0).toFixed(2);
  });

  const getTotalTax2Amount = computed(() => {
    const taxType2 = state.taxes[1]?.type;
    if (!taxType2) return 0;

    return state.orderItems.reduce((total, item) => {
      // Skip items with tax included in price
      if (item.isIncludeTaxChecked) {
        return total;
      }
      
      const taxRate = item.taxRates.find(tax => tax.type === taxType2)?.rate || 0;
      return total + (item.Price * item.Qty * (taxRate / 100));
    }, 0).toFixed(2);
  });

  // Get all order items
  const getOrderItems = computed(() => state);

  // Get the total price of the order (excluding invalid price items)
  const getOrderTotal = computed(() => {
    // Calculate the subtotal of all items in the order, excluding invalid price items
    const subtotal = Array.isArray(state.orderItems)
      ? state.orderItems.reduce((total, item) => {
          if (item.isIncludeTaxChecked) {
            return total + (parseFloat(item?.taxInclusivePrice) * item.Qty);
          }
          // Skip items with invalid prices
          const itemId = item.uniqueId || item.ItemId;
          if (invalidPriceFields.value.has(itemId)) {
            return total;
          }
          return total + (parseFloat(item.Price) * item.Qty);
        }, 0)
      : 0;

    // Get the total GST and PST amounts
    const tax1Total = parseFloat(getTotalTax1Amount.value) || 0;
    const tax2Total = parseFloat(getTotalTax2Amount.value) || 0;

    // Calculate the total order amount including taxes
    const total = subtotal + tax1Total + tax2Total;

    // Return the total rounded to 2 decimal places
    return parseFloat(total.toFixed(2));
  });

  // Update the discount for a specific order item and recalculate the price
  function updateDiscountPercentage(item, discount) {
    const existingItem = state.orderItems.find(orderItem =>
      orderItem.ItemId === item.ItemId
    );
    if (existingItem) {
      existingItem.Discount = discount;
      // Calculate price based on discount and original price
      const newPrice = Number((existingItem.OriginalPrice * (1 - discount / 100)).toFixed(2));
      
      // If tax is included in price, update both taxInclusivePrice and base price
      if (existingItem.isIncludeTaxChecked) {
        existingItem.taxInclusivePrice = newPrice;
        // Recalculate base price from tax-inclusive price
        const totalTaxRate = existingItem.taxRates.reduce((sum, tax) => sum + (tax.rate || 0), 0);
        if (totalTaxRate > 0) {
          existingItem.Price = Number((newPrice / (1 + totalTaxRate / 100)).toFixed(2));
        } else {
          existingItem.Price = newPrice;
        }
      } else {
        existingItem.Price = newPrice;
      }
      
      calculateTaxes();
      saveOrderItemsToLocalStorage();
    }
  }

  // Update the original price of the item and recalculate the discount
  function updateOriginalPrice(item, originalPrice) {
    const existingItem = state.orderItems.find(orderItem =>
      orderItem.ItemId === item.ItemId
    );
    if (existingItem) {
      existingItem.OriginalPrice = originalPrice;
      // Recalculate discount based on original price
      existingItem.Discount = ((1 - existingItem.Price / originalPrice) * 100).toFixed(2);

      saveOrderItemsToLocalStorage();
    }
  }

  // Reset the discount for a specific order item
  function resetDiscount(item) {
    const existingItem = state.orderItems.find(orderItem =>
      orderItem.ItemId === item.ItemId
    );
    if (existingItem) {
      existingItem.Discount = 0;
      
      // If tax is included in price, update both taxInclusivePrice and base price
      if (existingItem.isIncludeTaxChecked) {
        existingItem.taxInclusivePrice = existingItem.OriginalPrice;
        // Recalculate base price from tax-inclusive price
        const totalTaxRate = existingItem.taxRates.reduce((sum, tax) => sum + (tax.rate || 0), 0);
        if (totalTaxRate > 0) {
          existingItem.Price = Number((existingItem.OriginalPrice / (1 + totalTaxRate / 100)).toFixed(2));
        } else {
          existingItem.Price = existingItem.OriginalPrice;
        }
      } else {
        existingItem.Price = existingItem.OriginalPrice;
      }
      
      saveOrderItemsToLocalStorage();
    }
  }

  // Calculate the total discount percentage of all items
  const getTotalDiscountPercentage = computed(() => {
    if (state.orderItems.length === 0) return 0;
    return state.orderItems.reduce((totalDiscount, item) =>
      totalDiscount + parseFloat(item.Discount || 0), 0) / state.orderItems.length;
  });

  const isOverallDiscount = computed(() => {
    return state.overallDiscount > 0;
  });

  const getGrandTotalForOverallDiscount = computed(() => {
    // Calculate subtotal with full precision
    const subtotal = state.orderItems.reduce((total, item) => {
      return total + (Number(item.OriginalPrice) * Number(item.Qty));
    }, 0);

    const totalTax = state.taxes.reduce((taxTotal, tax) => {
      return taxTotal + state.orderItems.reduce((itemTotal, item) => {
        const taxRate = Number(item.taxRates.find(t => t.type === tax.type)?.rate || 0);
        return itemTotal + (Number(item.OriginalPrice) * Number(item.Qty) * taxRate / 100);
      }, 0);
    }, 0);

    const discountMultiplier = 1 - (Number(state.overallDiscount) / 100);
    const finalTotal = (subtotal + totalTax) * discountMultiplier;

    return Math.round((finalTotal + Number.EPSILON) * 100) / 100;
  });



  // Apply the overall discount to each item
  function applyOverallDiscount(overallDiscountPercentage) {
    state.overallDiscount = overallDiscountPercentage;

    state.orderItems.forEach(item => {
      const originalPrice = item.OriginalPrice;
      const newPrice = Number((originalPrice * (1 - overallDiscountPercentage / 100)).toFixed(2));
      
      // If tax is included in price, update both taxInclusivePrice and base price
      if (item.isIncludeTaxChecked) {
        item.taxInclusivePrice = newPrice;
        // Recalculate base price from tax-inclusive price
        const totalTaxRate = item.taxRates.reduce((sum, tax) => sum + (tax.rate || 0), 0);
        if (totalTaxRate > 0) {
          item.Price = Number((newPrice / (1 + totalTaxRate / 100)).toFixed(2));
        } else {
          item.Price = newPrice;
        }
      } else {
        item.Price = newPrice;
      }
      
      calculateTaxes();
    });
    // console.log('Total Before', state.total.toFixed(2));
    // console.log('Total After', state.total.toFixed(2));
    console.log('Grand Total From Original Price', getGrandTotalForOverallDiscount.value);
    saveOrderItemsToLocalStorage();
  }

  function addToApprovalList({ ManagerId, ManagerApprovalId, ItemId, isOverallDiscount }) {
    // Create an approval object with the required fields
    const approval = {
      ManagerId: ManagerId,
      ManagerApprovalId: ManagerApprovalId,
      ItemId: ItemId || null,
      isOverallDiscount: isOverallDiscount > 0 ? true : false,
      id: ''
    };

    // Add the new approval entry to the state.approvalList
    state.approvalList.push(approval);

    // Optionally, persist the updated approvalList to local storage
    saveOrderItemsToLocalStorage();

    // Notify that approval has been added (optional, for debugging)
    console.log('Approval added:', approval);
  }

  // Save orderItems to localStorage
  function saveOrderItemsToLocalStorage() {
    console.log('Saving order items to localStorage');
    localStorage.setItem('newOrder', JSON.stringify(state));
  }

  function submitCommentToStore(commentText, managerUser, item) {
    const isCommentProvided = commentText.trim() !== '';
    const existingCommentIndex = state.comments.findIndex(comment => comment.item.sku === item.Sku);

    // Create the comment object regardless of whether the text is provided or not
    const newComment = {
      item: {
        name: item?.ItemName ?? 'Unknown Item',
        price: item?.Price ?? 0,
        imageUrl: item?.ItemImage ?? '',
        sku: item?.Sku ?? 'Unknown SKU',
        discount: item.Discount,
      },
      text: commentText.trim(),
      userId: managerUser,
      timestamp: new Date().toISOString()
    };

    // Update or add the new comment in the state.comments array
    if (existingCommentIndex > -1) {
      state.comments[existingCommentIndex] = newComment;
    } else {
      state.comments.push(newComment);
    }

    // Apply the discount only if a valid comment is provided
    if (isCommentProvided) {
      const discountValue = item.Discount || 0;
      updateDiscountPercentage(item, discountValue);
      addToApprovalList({
        ManagerId: managerUser,
        ItemId: item?.Sku,
        ManagerApprovalId: '',
        isOverallDiscount: state.overallDiscount
      })

    } else {
      resetDiscount(item);
    }

    saveOrderItemsToLocalStorage();
    window.dispatchEvent(new Event('comment-saved'));
  }


  // Iso date format   
  const formatDate = (timestamp) => {
    let date;
    if (typeof timestamp === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(timestamp)) {
      const [year, month, day] = timestamp.split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(timestamp);
    }
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/ /g, '-');
  };

  const draftOrders = ref([]);
  const getDefaultCustomer = () => ({
    id: '',
    name: '',
    lastName: '',
    phone: '',
    email: '',
    note: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });

  const cloneDraftData = (value) => {
    if (value === undefined) return undefined;
    return JSON.parse(JSON.stringify(value));
  };

  // Save the current state as a draft
  function saveOrderAsDraft() {
    if (state.orderItems.length === 0) {
      console.log('No active order items to save as draft');
      toast.error('Cannot save an empty order as draft!');
      return;
    }

    // Keep a full snapshot so manual order state can be restored correctly.
    const draft = {
      ...cloneDraftData(state),
      selectedSalesPerson: cloneDraftData(selectedSalesPerson.value),
      applySelectedSalesPersonForAll: applySelectedSalesPersonForAll.value,
      timestamp: Date.now(),
    };

    // Add the new draft to the draftOrders array
    draftOrders.value.push(draft);

    // Save the updated draft orders to localStorage
    localStorage.setItem('draftOrders', JSON.stringify(draftOrders.value));

    // Clear the current state (active order)
    state.InvoiceId = null;
    state.orderItems = [];
    state.approvalList = [];
    state.overallDiscount = 0;
    state.customer = getDefaultCustomer();
    state.comments = [];
    state.payments = [];
    state.total = 0;
    state.taxes = getProductStoreTaxes();
    state.receiptForReprint = null;
    state.returnPrint = null;
    state.couponCode = null;
    state.isManualOrder = false;
    state.addManualOrder = false;
    state.pickUpInStore = false;
    state.pickupLocation = null;
    selectedSalesPerson.value = null;
    applySelectedSalesPersonForAll.value = false;

    // Remove the active order from localStorage
    localStorage.removeItem('newOrder');
    toast.success('Order saved as draft!');
  }

  // Fetch the draft orders from localStorage and update the reactive array
  function fetchDraftOrders() {
    const savedDrafts = JSON.parse(localStorage.getItem('draftOrders')) || [];
    draftOrders.value = savedDrafts;
    // console.log('Fetched drafts:', draftOrders.value);
    return draftOrders.value;
  }

  // Load a draft order back into newOrder
  function loadDraftOrder(draftIndex) {
    const selectedDraft = draftOrders.value[draftIndex];

    if (selectedDraft) {
      const draftSnapshot = cloneDraftData(selectedDraft);
      const mergedOrderItems = Array.isArray(state.orderItems)
        ? cloneDraftData(state.orderItems)
        : [];

      const draftTaxes = Array.isArray(draftSnapshot.taxes) && draftSnapshot.taxes.length > 0
        ? draftSnapshot.taxes.map(normalizeTax)
        : getProductStoreTaxes();

      const draftOrderItems = Array.isArray(draftSnapshot.orderItems) ? draftSnapshot.orderItems : [];
      draftOrderItems.forEach((draftItem) => {
        const draftItemTaxRates = Array.isArray(draftItem.taxRates) && draftItem.taxRates.length > 0
          ? draftItem.taxRates
          : draftTaxes.map(tax => ({
            type: tax.type,
            rate: tax.rate,
            amount: 0,
          }));

        const existingItemIndex = mergedOrderItems.findIndex((orderItem) => {
          if (draftItem.Sku === 'MISC-2' || draftItem.Sku?.startsWith('BSUR')) {
            return orderItem.uniqueId === draftItem.uniqueId;
          }
          return orderItem.ItemId === draftItem.ItemId;
        });

        if (existingItemIndex !== -1) {
          const existingItem = mergedOrderItems[existingItemIndex];
          existingItem.Qty += Number(draftItem.Qty || 0);
          existingItem.taxRates = draftItemTaxRates;
        } else {
          mergedOrderItems.push({
            ...draftItem,
            taxRates: draftItemTaxRates,
          });
        }
      });

      state.orderItems = mergedOrderItems;
      state.approvalList = [
        ...(Array.isArray(state.approvalList) ? state.approvalList : []),
        ...(Array.isArray(draftSnapshot.approvalList) ? draftSnapshot.approvalList : []),
      ];
      state.comments = [
        ...(Array.isArray(state.comments) ? state.comments : []),
        ...(Array.isArray(draftSnapshot.comments) ? draftSnapshot.comments : []),
      ];
      state.payments = [
        ...(Array.isArray(state.payments) ? state.payments : []),
        ...(Array.isArray(draftSnapshot.payments) ? draftSnapshot.payments : []),
      ];
      state.overallDiscount = Math.max(
        Number(state.overallDiscount || 0),
        Number(draftSnapshot.overallDiscount || 0)
      );

      const hasCurrentCustomer = Boolean(state.customer?.id || state.customer?.name || state.customer?.phone || state.customer?.email);
      if (!hasCurrentCustomer && draftSnapshot.customer) {
        state.customer = { ...getDefaultCustomer(), ...draftSnapshot.customer };
      }

      if (!state.InvoiceId && draftSnapshot.InvoiceId) {
        state.InvoiceId = draftSnapshot.InvoiceId;
      }

      if (state.taxes.length === 0 && draftTaxes.length > 0) {
        state.taxes = draftTaxes;
      }

      state.isManualOrder = Boolean(state.isManualOrder || draftSnapshot.isManualOrder);
      state.addManualOrder = Boolean(state.addManualOrder || draftSnapshot.addManualOrder);
      if (!state.pickupLocation && draftSnapshot.pickupLocation) {
        state.pickupLocation = draftSnapshot.pickupLocation;
      }
      state.pickUpInStore = Boolean(state.pickUpInStore || draftSnapshot.pickUpInStore);
      if (!selectedSalesPerson.value && draftSnapshot.selectedSalesPerson) {
        selectedSalesPerson.value = draftSnapshot.selectedSalesPerson;
      }
      applySelectedSalesPersonForAll.value = Boolean(
        applySelectedSalesPersonForAll.value || draftSnapshot.applySelectedSalesPersonForAll
      );

      // Recalculate everything
      calculateTaxes();
      saveOrderItemsToLocalStorage();

      // Remove the draft
      draftOrders.value.splice(draftIndex, 1);
      localStorage.setItem('draftOrders', JSON.stringify(draftOrders.value));

      toast.success('Draft order restored successfully!');
    } else {
      toast.error('Failed to load draft order.');
    }
  }

  // Remove a draft order
  function removeDraftOrder(draftIndex) {
    if (draftIndex >= 0 && draftIndex < draftOrders.value.length) {
      draftOrders.value.splice(draftIndex, 1);
      // Update localStorage with the modified draftOrders array
      localStorage.setItem('draftOrders', JSON.stringify(draftOrders.value));
      toast.success('Draft order removed successfully!');
    } else {
      toast.error('Invalid draft order index.');
    }
  }

  const showDraftList = ref(false);
  // Toggle the draft list visibility
  function toggleDraftList() {
    showDraftList.value = !showDraftList.value;
    console.log('order notification toggeled');
  }

  function clearOrder() {
    console.log('Clearing order and removing newOrder from localStorage');

    // Reset the state to its initial values
    state.InvoiceId = null;
    state.orderItems = [];
    state.approvalList = [];
    state.overallDiscount = 0;
    state.pickUpInStore = false;
    state.pickupLocation = null;
    state.customer = {
      id: '',
      name: '',
      phone: '',
      email: '',
      note: ''
    };
    state.comments = [];
    state.payments = [];
    state.total = 0;
    state.taxes = getProductStoreTaxes();
    state.returnPrint = null;
    selectedSalesPerson.value = null;
    applySelectedSalesPersonForAll.value = false;
    state.addManualOrder = false;

    localStorage.removeItem('newOrder');

    toast.success('Order cleared successfully!');
  }

  // // Save Receipt For Reprint
  // const receiptKey = 'savedReceipts';
  // const maxReceiptToSave = 20;

  // // const isSavedReceipt = ref(false);
  // function safeStringify(obj) {
  //   const seen = new WeakSet();
  //   return JSON.stringify(obj, (key, value) => {
  //     if (typeof value === 'object' && value !== null) {
  //       if (seen.has(value)) return '[Circular]';
  //       seen.add(value);
  //     }
  //     return value;
  //   });
  // }
  // // Function to save a receipt to local storage
  // function saveReceiptToStorage(receiptData) {
  //   try {
  //     const existingReceipts = JSON.parse(localStorage.getItem(receiptKey)) || [];
  //     receiptData = safeStringify(receiptData);
  //     receiptData = JSON.parse(receiptData);
  //     const updatedReceipts = [receiptData, ...existingReceipts];

  //     if (updatedReceipts.length > maxReceiptToSave) {
  //       updatedReceipts.length = maxReceiptToSave;
  //     }

  //     localStorage.setItem(receiptKey, JSON.stringify(updatedReceipts));
  //     updateReceiptStatus();
  //   } catch (error) {
  //     console.error('Error saving receipt to local storage:', error);
  //   }
  // }

  // Save Receipt For Reprint
  const receiptKey = 'savedReceipts';
  const receiptRetentionDays = 14;

  function safeStringify(obj) {
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return '[Circular]';
        seen.add(value);
      }
      return value;
    });
  }

  // Function to save a receipt to local storage
  function saveReceiptToStorage(receiptData) {
    try {
      const existingReceipts = JSON.parse(localStorage.getItem(receiptKey)) || [];

      // Ensure receipt is safe and add a timestamp
      receiptData = safeStringify(receiptData);
      receiptData = JSON.parse(receiptData);
      receiptData._savedAt = Date.now();

      // Add new receipt at the top
      let updatedReceipts = [receiptData, ...existingReceipts];

      // Calculate cutoff time (14 days ago)
      const cutoff = Date.now() - (receiptRetentionDays * 24 * 60 * 60 * 1000);

      // Keep only receipts newer than cutoff
      updatedReceipts = updatedReceipts.filter(r => r._savedAt >= cutoff);

      // Save updated receipts
      localStorage.setItem(receiptKey, JSON.stringify(updatedReceipts));

      updateReceiptStatus();
    } catch (error) {
      console.error('Error saving receipt to local storage:', error);
    }
  }


  function checkReceipts() {
    try {
      const receipts = JSON.parse(localStorage.getItem(receiptKey)) || [];
      console.log('Saved receipts:', receipts);
      return receipts.length > 0;
    } catch (error) {
      console.error('Error checking receipts:', error);
      return false;
    }
  }

  const isSavedReceipt = ref(checkReceipts());

  function updateReceiptStatus() {
    isSavedReceipt.value = checkReceipts();
  }

  // Function to get all saved receipts
  function getSavedReceipts() {
    try {
      return JSON.parse(localStorage.getItem(receiptKey)) || [];
    } catch (error) {
      console.error('Error retrieving receipts from local storage:', error);
      return [];
    }
  }

  //Deduct item qty is user sales an item starts here

  const inventoryKey = 'inventoryState';
  const currentFileKey = 'currentInventoryFile';

  // Save inventory state to localStorage
  function saveInventoryState() {
    // Load existing inventory state from localStorage
    const existingState = JSON.parse(localStorage.getItem(inventoryKey)) || {};

    const newInventoryState = { ...existingState };

    state.orderItems.forEach(item => {
      const product = productStore.products.find(p => p.Sku === item.ItemId);
      if (product) {
        newInventoryState[item.ItemId] = product.MaxQty;
      }
    });

    localStorage.setItem(inventoryKey, JSON.stringify(newInventoryState));
  }

  // Load inventory state from localStorage and apply to products
  function loadInventoryState() {
    const savedState = localStorage.getItem(inventoryKey);
    if (savedState) {
      const inventoryState = JSON.parse(savedState);
      console.log('Inventory object state', inventoryState);
      // Only update products that exist in the saved state
      Object.keys(inventoryState).forEach(sku => {
        const product = productStore.products.find(p => p.Sku === sku);
        if (product) {
          product.MaxQty = inventoryState[sku];
        }
      });
    }
  }

  // Reset inventory state when a new file is loaded
  function resetInventoryState(fileName) {
    const currentFile = localStorage.getItem(currentFileKey);
    if (currentFile !== fileName) {
      localStorage.removeItem(inventoryKey);
      localStorage.setItem(currentFileKey, fileName);
      deleteCachedProduct();
    }
  }

  //Deduct quantity
  function deductQuantities() {
    this.state.orderItems.forEach(item => {
      const product = productStore.products.find(p => p.Sku === item.ItemId);
      if (product) {
        product.MaxQty -= item.Qty;
        if (product.MaxQty < 0) product.MaxQty = 0;
        console.log('New Max Qty', product.MaxQty);
      }
    });
    saveInventoryState();
  }

  //Handles delete cached products from localStorage
  const deleteCachedProduct = () => {
    localStorage.removeItem('cachedProducts');
  };

  // Check if the order is manual order
  const isManualOrder = computed(() => {
    return state.InvoiceId !== null && state.InvoiceId !== undefined;
  });

  // Check if sales person is selected 
  const isSalesPersonSelected = computed(() => {
    return selectedSalesPerson.value !== null && selectedSalesPerson.value !== undefined;
  });

  const isSalesPersonForManualOrder = computed(() => {
    return state.orderItems.some(item => item.SalesPersonId !== null && item.SalesPersonId !== undefined);
  });

  // Check if there is an item with 0.00 price

  const hasZeroPriceItem = computed(() => {
    return state.orderItems.some(item => item.OriginalPrice === 0);
  });


  //  inlcude tax toggle 
  const checkIncludeTax = (item) => {
    const wasChecked = item.isIncludeTaxChecked;
    item.isIncludeTaxChecked = !item.isIncludeTaxChecked;
    
    if (item.isIncludeTaxChecked) {
      // When toggling to true: store the current price as tax-inclusive price
      // and back-calculate the base price
      if (!item.taxInclusivePrice) {
        item.taxInclusivePrice = item.Price;
      }
      
      // Calculate total tax rate for this item
      const totalTaxRate = item.taxRates.reduce((sum, tax) => sum + (tax.rate || 0), 0);
      
      // Back-calculate base price: basePrice = taxInclusivePrice / (1 + totalTaxRate/100)
      if (totalTaxRate > 0) {
        item.Price = Number((item.taxInclusivePrice / (1 + totalTaxRate / 100)).toFixed(2));
        // item.Discount = totalTaxRate;
        // item.OriginalPrice = item.Price;
      } else {
        // If no tax rate, keep the price as is
        item.Price = item.taxInclusivePrice;
      }
    } else {
      // When toggling to false: restore the tax-inclusive price
      if (item.taxInclusivePrice) {
        item.Price = item.taxInclusivePrice;
        item.taxInclusivePrice = null;
      }
    }
    
    // calculateTaxes();
    saveOrderItemsToLocalStorage();
  };

  // Clear cashe
  const clearCache = () => {
    // Clear localStorage
    // localStorage.clear();
    try {
      Object.keys(localStorage).forEach(key => {
        if (key !== 'savedReceipts') {
          localStorage.removeItem(key);
        }
      });
      console.log("Cache cleared except.");
    } catch (error) {
      console.error("Error while clearing cache:", error);
    }

    // Clear Cache API (if Service Worker used)
    if ('caches' in window) {
      caches.keys().then(names => names.forEach(name => caches.delete(name)));
    }
    window.location.reload(true);
    // toast.success("Cache cleared. The app will now reloaded.");
  };

  onMounted(() => {
    loadOrderItemsFromLocalStorage();
    updateReceiptStatus();
    loadInventoryState();
  });

  watch(
    () => productStore.taxes,
    (taxes) => {
      if (!state.orderItems.length && Array.isArray(taxes) && taxes.length > 0) {
        state.taxes = getProductStoreTaxes();
        saveOrderItemsToLocalStorage();
      }
    },
    { deep: true }
  );

  const isManualOrderLoading = ref(false);
  const isThisPaidManualOrder = ref(localStorage.getItem('isThisPaidManualOrder') === 'true');

  function setIsThisPaidManualOrder(value) {
    isThisPaidManualOrder.value = value;
    localStorage.setItem('isThisPaidManualOrder', value);
  }

  return {
    state,
    addOrderItem,
    incrementOrderItem,
    decrementOrderItem,
    deleteOrderItem,
    calculateTaxes,
    updateDiscountPercentage,
    resetDiscount,
    getTotalDiscountPercentage,
    getOrderItems,
    getOrderTotal,
    getGrandTotalForOverallDiscount,
    getTotalTax1Amount,
    getTotalTax2Amount,
    selectedSalesPerson,
    setSelectedSalesPerson,
    applySalesPersonToAllItems,
    applyOverallDiscount,
    saveOrderAsDraft,
    fetchDraftOrders,
    loadDraftOrder,
    showDraftList,
    toggleDraftList,
    draftOrders,
    submitCommentToStore,
    loadOrderItemsFromLocalStorage,
    removeDraftOrder,
    // recalculateTotal,
    applyRealtimeItemUpdate,
    updateOriginalPrice,
    updateGstRate,
    updatePstRate,
    updateOrderTotal,
    saveOrderItemsToLocalStorage,
    formatDate,
    updateItemTaxRate,
    getItemTaxAmounts,
    resetTaxRate,
    clearOrder,
    saveReceiptToStorage,
    isSavedReceipt,
    getSavedReceipts,
    clearReceiptForReprint,
    setReceiptForReprint,
    isOverallDiscount,
    deductQuantities,
    resetInventoryState,
    deleteCachedProduct,
    clearOrderItems,
    setCustomer,
    setTaxesFManualOrder,
    setSaleOrderNumber,
    setSalesPersonId,
    isManualOrder,
    clearReturnPrint,
    isSalesPersonSelected,
    isSalesPersonForManualOrder,
    setOverallDiscount,
    hasZeroPriceItem,
    clearCache,
    addInvalidPriceField,
    removeInvalidPriceField,
    hasInvalidPrice,
    checkIncludeTax,
    clearCustomer,
    isManualOrderLoading,
    isThisPaidManualOrder,
    setIsThisPaidManualOrder,
  };
});